import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Radio, RefreshCcw, Plus, FolderKanban, Activity, CircleCheck, Users,
} from 'lucide-react';
import {
  The, DauThe, TheSoLieu, NutPhu, NutChinh, NhanTrangThai, AnhDaiDien,
  ChuaCoDuLieu, DangTai,
} from '../components/ui';
import { api, LoiMayChu } from '../services/api';
import type { TrangThaiExtension, MucTongQuanTK, Nhom, ChienDich, TaiKhoanFB, SoDem } from '../services/kieu';
import { KHONG_AP_DUNG } from '../services/kieu';
import { avatarActor, tenActor } from '../services/taiKhoan';

/**
 * Hiện một số đếm theo đúng ba trạng thái máy chủ phân biệt.
 *
 * Gộp cả ba thành `—` là vứt đi thông tin người dùng cần để quyết định:
 *
 *   số      — đã đo được, dùng luôn. `0` là sự thật, KHÔNG đổi thành `—`.
 *   `—`     — không áp dụng (Page không có bạn bè). Không cần làm gì.
 *   `·`     — chưa đo được. Bấm "Làm mới" là có.
 *
 * Lỗi cũ: cột Bạn viết cứng dấu `—` (TongQuan.tsx:189 bản trước), nên dù máy
 * chủ trả 610 bạn thì màn hình vẫn hiện gạch ngang.
 */
function HienSo({ gia, laPage }: { gia: SoDem; laPage: boolean }) {
  if (gia === KHONG_AP_DUNG) {
    return (
      <span title={laPage ? 'Trang không có danh sách bạn bè' : 'Không áp dụng'} className="text-slate-400">
        —
      </span>
    );
  }
  if (gia === null || gia === undefined) {
    return <span title="Chưa đo được — bấm Làm mới" className="text-slate-300 dark:text-slate-600">·</span>;
  }
  return <>{gia.toLocaleString('vi-VN')}</>;
}

interface Props {
  ext: TrangThaiExtension | null;
  taiKhoan: TaiKhoanFB | null;
  entitlementActive?: boolean;
  onLamMoi: () => void;
  onTaoBai: () => void;
}

export function ManTongQuan({ ext, taiKhoan, entitlementActive, onLamMoi, onTaoBai }: Props) {
  const [danhBa, setDanhBa] = useState<MucTongQuanTK[]>([]);
  const [nhom, setNhom] = useState<Nhom[]>([]);
  const [chienDich, setChienDich] = useState<ChienDich[]>([]);
  const [dangTai, setDangTai] = useState(true);
  const [loi, setLoi] = useState<string | null>(null);
  const requestGeneration = useRef(0);
  const loadedActorUid = useRef(taiKhoan?.uid);

  const tai = useCallback(async () => {
    const generation = ++requestGeneration.current;
    if (loadedActorUid.current !== taiKhoan?.uid) {
      loadedActorUid.current = taiKhoan?.uid;
      setNhom([]);
      setChienDich([]);
      setDanhBa([]);
      setLoi(null);
    }
    setDangTai(true);
    // Ba lời gọi độc lập: một cái hỏng không được làm chết hai cái kia.
    // Bản cũ dùng Promise.all nên chỉ cần nhóm chưa đồng bộ là cả màn trắng.
    //
    // Dùng `tongQuanTaiKhoan()` chứ KHÔNG phải `danhBa()`: `/directory` là sổ
    // danh bạ thuần, không mang số bạn bè và số nhóm. Bảng từng gọi nhầm nó nên
    // hai cột số luôn hiện `—` dù máy chủ vẫn trả đúng 44 nhóm ở `/overview`.
    const [db, nh, cd] = await Promise.allSettled([
      api.tongQuanTaiKhoan(), api.nhom(taiKhoan?.uid), api.chienDich(),
    ]);
    if (requestGeneration.current !== generation) return;
    if (db.status === 'fulfilled') setDanhBa(db.value.accounts || []);
    if (nh.status === 'fulfilled') setNhom(nh.value.groups || []);
    if (cd.status === 'fulfilled') setChienDich(cd.value.campaigns || []);

    const hong = [db, nh, cd].find(x => x.status === 'rejected') as PromiseRejectedResult | undefined;
    setLoi(hong ? (hong.reason instanceof LoiMayChu ? hong.reason.message : 'Một phần dữ liệu chưa tải được.') : null);
    setDangTai(false);
  }, [taiKhoan?.uid]);

  // Revalidate once when authoritative entitlement changes. Successful reads
  // clear stale errors; session recovery alone must not hide a current API error.
  useEffect(() => {
    tai();
    return () => { requestGeneration.current++; };
  }, [tai, entitlementActive]);

  const lamMoiTatCa = () => { tai(); onLamMoi(); };

  const dangOnline = new Set((ext?.accounts || []).map(a => String(a.uid)));
  const soTacVu = chienDich.reduce((s, c) => s + (c.pending || 0) + (c.paused || 0), 0);
  const noiDuoc = Boolean(taiKhoan && (taiKhoan.identityContext ? taiKhoan.identityContext.state === 'READY' : taiKhoan.identityState === 'VERIFIED'));

  return (
    <div className="mx-auto max-w-[1440px] animate-fade-in space-y-5">
      {loi && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">
          {loi}
        </div>
      )}

      {/* Tài khoản đang hoạt động */}
      <The className="p-5">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <AnhDaiDien src={avatarActor(taiKhoan)} ten={tenActor(taiKhoan)} kichThuoc="h-16 w-16" />
            <div className="min-w-0">
              <div
                className={
                  'inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold ' +
                  (noiDuoc
                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
                    : 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400')
                }
              >
                <Radio className="h-3 w-3" />
                {noiDuoc ? 'Tài khoản đang hoạt động' : 'Đang chờ tài khoản Facebook'}
              </div>
              <h2 className="mt-2 truncate text-xl font-bold text-slate-900 dark:text-white">
                {tenActor(taiKhoan)}
              </h2>
              {taiKhoan?.isPage && !taiKhoan.name && (
                <p className="mt-1 text-xs font-semibold text-amber-600 dark:text-amber-400">
                  Chưa đồng bộ được tên Page từ Facebook. Vui lòng mở Page hoặc tải lại Extension để cập nhật.
                </p>
              )}
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                UID:{' '}
                <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                  {taiKhoan?.uid || '—'}
                </span>
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <NutPhu onClick={lamMoiTatCa}>
              <RefreshCcw className="h-3.5 w-3.5" />
              Làm mới
            </NutPhu>
            <NutChinh onClick={onTaoBai}>
              <Plus className="h-4 w-4" />
              Tạo bài đăng
            </NutChinh>
          </div>
        </div>
      </The>

      {/* Ba thẻ số liệu.
          Hai thẻ đầu đếm từ `nhom` / `chienDich` — hai mảng khởi tạo rỗng, nên
          phải truyền `dangTai`, nếu không chúng khẳng định "0" trong lúc chờ API.
          Thẻ thứ ba đọc từ prop `ext` do App truyền xuống: nó KHÔNG phụ thuộc ba
          lời gọi trong `tai()`, nên cố tình không nhận `dangTai`. */}
      <div className="grid gap-3 sm:grid-cols-3">
        <TheSoLieu icon={<FolderKanban className="h-5 w-5" />} so={nhom.length} nhan="Nhóm đã đồng bộ" dangTai={dangTai} />
        <TheSoLieu icon={<Activity className="h-5 w-5" />} so={soTacVu} nhan="Tác vụ gần đây" dangTai={dangTai} />
        <TheSoLieu
          icon={<CircleCheck className="h-5 w-5" />}
          so={noiDuoc ? 'Sẵn sàng' : 'Chờ kết nối'}
          nhan="Trạng thái tài khoản"
        />
      </div>

      {/* Bảng tài khoản */}
      <The>
        <DauThe
          icon={<Users className="h-4 w-4" />}
          tieuDe="Tài khoản"
          dem={danhBa.length}
          phai={
            <NutPhu onClick={lamMoiTatCa}>
              <RefreshCcw className="h-3.5 w-3.5" />
              Làm mới
            </NutPhu>
          }
        />

        {dangTai ? (
          <DangTai dong={3} />
        ) : danhBa.length === 0 ? (
          /* Bảng chỉ hiện tài khoản đang kết nối (và tài khoản vừa rớt trong
             24h). Rỗng thật nghĩa là chưa ai kết nối bao giờ — nói thẳng việc
             cần làm, đừng để người dùng đoán. */
          <ChuaCoDuLieu
            chu="Chưa có tài khoản nào kết nối."
            goiY="Mở Extension FBMax và đăng nhập Facebook để bắt đầu."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400 dark:border-slate-700/60">
                    <th className="px-5 py-2.5 font-semibold">Tên</th>
                    <th className="px-3 py-2.5 font-semibold">UID</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Bạn</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Nhóm</th>
                    <th className="px-5 py-2.5 text-right font-semibold">Trạng thái</th>
                  </tr>
                </thead>
                <tbody>
                  {danhBa.map((tk) => {
                    // Ưu tiên trạng thái thật từ /api/extension/status: nó tươi
                    // hơn ảnh chụp lúc dựng danh sách.
                    const on = dangOnline.has(String(tk.uid)) || tk.online === true;
                    return (
                      <tr
                        key={tk.uid}
                        className={
                          'border-b border-slate-50 last:border-0 dark:border-slate-700/50 ' +
                          (on ? '' : 'opacity-55')
                        }
                      >
                        <td className="px-5 py-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <AnhDaiDien src={tk.avatar} ten={tk.name || tk.uid} />
                            <div className="min-w-0">
                              <p className="truncate font-semibold text-slate-800 dark:text-slate-100">
                                {tk.name || '(chưa có tên)'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">
                          {tk.uid}
                        </td>
                        <td className="px-3 py-3 text-right font-semibold text-slate-700 tabular-nums dark:text-slate-200">
                          <HienSo gia={tk.friendCount} laPage={tk.isPage} />
                        </td>
                        <td className="px-3 py-3 text-right font-semibold text-slate-700 tabular-nums dark:text-slate-200">
                          <HienSo gia={tk.groupCount} laPage={tk.isPage} />
                        </td>
                        <td className="px-5 py-3 text-right">
                          {on
                            ? <NhanTrangThai chu="Đang kết nối" mau="xanh" />
                            : <NhanTrangThai chu="Vừa mất kết nối" mau="vang" />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="border-t border-slate-100 px-5 py-3 text-xs text-slate-400 dark:border-slate-700/60">
              Chỉ hiện tài khoản đang kết nối. Dấu <span className="font-semibold">—</span> nghĩa là không áp dụng
              (Trang không có bạn bè); dấu <span className="font-semibold">·</span> nghĩa là chưa đo được, bấm Làm mới.
            </p>
          </>
        )}
      </The>
    </div>
  );
}
